import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { LANDING } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

const initialForm = { name: "", email: "", company: "", vertical: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.vertical || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. Our team will reach out shortly.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative bg-slate-50 border-t border-slate-200 py-28 sm:py-36">
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeUp}
          className="lg:col-span-5"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#1C8FC4] mb-6">05 / Contact</p>
          <h2 className="font-heading font-bold uppercase text-[#1A2E4A] text-4xl sm:text-5xl tracking-tight mb-10">
            Spec a valve. Start a project.
          </h2>

          <div className="flex flex-col gap-8">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Address</p>
              <p className="font-body text-slate-900 text-sm leading-relaxed" data-testid="contact-address">
                SUMATECH INNOVATIONS LLP
                <br />
                603 Vakratunda Corporate Park, Vishweshwar Nagar
                <br />
                Goregaon East, Mumbai 400063, Maharashtra, India
              </p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Phone</p>
              <p className="font-body text-slate-900 text-sm leading-relaxed" data-testid="contact-phone">
                <a href="tel:+912229271664" className="hover:text-[#1C8FC4] transition-colors">+91-22-2927 1664</a>
                {" "}&middot;{" "}
                <a href="tel:+912267255314" className="hover:text-[#1C8FC4] transition-colors">+91-22-6725 5314</a>
                <br />
                <a href="tel:+919979878086" className="hover:text-[#1C8FC4] transition-colors">+91-99798 78086</a>
                {" "}&middot;{" "}
                <a href="tel:+919904055588" className="hover:text-[#1C8FC4] transition-colors">+91-99040 55588</a>
              </p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Email</p>
              <p className="font-body text-slate-900 text-sm leading-relaxed" data-testid="contact-email">
                <a href="mailto:info@sumatechinnovations.com" className="hover:text-[#1C8FC4] transition-colors">info@sumatechinnovations.com</a>
                <br />
                <a href="mailto:sales@sumatechinnovations.com" className="hover:text-[#1C8FC4] transition-colors">sales@sumatechinnovations.com</a>
              </p>
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeUp}
          transition={{ delay: 0.15 }}
          className="lg:col-span-6 lg:col-start-7 flex flex-col gap-5"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Input
              data-testid={LANDING.contactFormName}
              placeholder="Full name *"
              value={form.name}
              onChange={handleChange("name")}
              className="rounded-none border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 h-12 focus-visible:ring-[#1C8FC4]"
            />
            <Input
              data-testid={LANDING.contactFormEmail}
              type="email"
              placeholder="Email *"
              value={form.email}
              onChange={handleChange("email")}
              className="rounded-none border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 h-12 focus-visible:ring-[#1C8FC4]"
            />
          </div>
          <Input
            data-testid={LANDING.contactFormCompany}
            placeholder="Company"
            value={form.company}
            onChange={handleChange("company")}
            className="rounded-none border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 h-12 focus-visible:ring-[#1C8FC4]"
          />
          <Select value={form.vertical} onValueChange={(v) => setForm((f) => ({ ...f, vertical: v }))}>
            <SelectTrigger
              data-testid={LANDING.contactFormVertical}
              className="rounded-none border-slate-300 bg-white text-slate-900 h-12"
            >
              <SelectValue placeholder="Vertical of interest *" />
            </SelectTrigger>
            <SelectContent className="rounded-none border-slate-300 bg-white text-slate-900">
              <SelectItem value="commercial">Commercial</SelectItem>
              <SelectItem value="industrial">Industrial</SelectItem>
              <SelectItem value="semiconductor">Semiconductor</SelectItem>
              <SelectItem value="not-sure">Not sure</SelectItem>
            </SelectContent>
          </Select>
          <Textarea
            data-testid={LANDING.contactFormMessage}
            placeholder="Tell us about your requirement *"
            value={form.message}
            onChange={handleChange("message")}
            className="rounded-none border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 min-h-[140px] focus-visible:ring-[#1C8FC4]"
          />
          <button
            type="submit"
            disabled={submitting}
            data-testid={LANDING.contactFormSubmit}
            className="mt-2 self-start inline-flex items-center gap-3 bg-[#FF6F3C] text-white px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#e85f2f] disabled:opacity-50 transition-colors duration-200"
          >
            {submitting ? "Sending..." : "Send Message"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;
